import {
  type CrosshairColor,
  type CrosshairStyle,
  type FpsCrosshairSettings,
  crosshairColorCss,
  loadCrosshairSettings,
  saveCrosshairSettings,
} from './FpsCrosshairSettings.js';
import { FpsHaptics } from './FpsHaptics.js';

const LS_BLOOM = 'stormsurge_fps_bloom';
const LS_SHADOWS = 'stormsurge_fps_shadows';
const LS_VOLUME = 'stormsurge_fps_volume';
const LS_PERF = 'stormsurge_fps_perf';
const LS_FOV = 'stormsurge_fps_fov';
const LS_SENS = 'stormsurge_fps_look_sens';
const LS_ADS_SENS = 'stormsurge_fps_ads_sens';
const LS_COLORBLIND = 'stormsurge_fps_colorblind_storm';
const LS_TOUCH_AIM = 'stormsurge_fps_touch_aim_assist';
const LS_REDUCED_MOTION = 'stormsurge_fps_reduced_motion';
const LS_HIT_FEEDBACK = 'stormsurge_fps_hit_feedback_intensity';
const LS_HUD_SCALE = 'stormsurge_fps_hud_scale';
const LS_BOT_DIFFICULTY = 'stormsurge_fps_bot_difficulty';

export type FpsHitFeedbackIntensity = 0.75 | 1 | 1.2;
export type FpsHudScale = number;
export type BotDifficultyTier = 'casual' | 'normal' | 'sweaty';

export const HUD_SCALE_MIN = 0.85;
export const HUD_SCALE_MAX = 1.12;
export const HUD_SCALE_DEFAULT = 1;

function clampHudScale(value: number): number {
  return Math.round(Math.max(HUD_SCALE_MIN, Math.min(HUD_SCALE_MAX, value)) * 100) / 100;
}

export type FpsGraphicsState = {
  bloom: boolean;
  shadowsHigh: boolean;
  /** 0–1 master SFX volume */
  masterVolume: number;
  /** Lower bloom strength + pixel ratio cap */
  performanceMode: boolean;
  /** Field of view (70–90) */
  fov: number;
  /** Look sensitivity multiplier (0.5–2) */
  lookSensitivity: number;
  /** ADS look sensitivity multiplier (0.25–1.5) */
  adsSensitivity: number;
  /** Storm ring / minimap use cyan instead of purple */
  colorblindStorm: boolean;
  /** Nudge aim toward bots while firing on touch (default on for touch devices) */
  touchAimAssist: boolean;
  /** Reduce camera/HUD motion and transient animation intensity. */
  reducedMotion: boolean;
  /** Hit marker and floating damage intensity multiplier. */
  hitFeedbackIntensity: FpsHitFeedbackIntensity;
  /** HUD scale multiplier applied through CSS. */
  hudScale: FpsHudScale;
  /** Bot AI difficulty preset (accuracy + reaction time). */
  botDifficulty: BotDifficultyTier;
};

const DEFAULT_GRAPHICS: FpsGraphicsState = {
  bloom: true,
  shadowsHigh: true,
  masterVolume: 0.85,
  performanceMode: false,
  fov: 76,
  lookSensitivity: 1,
  adsSensitivity: 0.7,
  colorblindStorm: false,
  touchAimAssist: false,
  reducedMotion: false,
  hitFeedbackIntensity: 1,
  hudScale: 1,
  botDifficulty: 'normal',
};

/** Read persisted settings; missing keys yield undefined. */
export function loadFpsGraphicsSettings(): Partial<FpsGraphicsState> {
  const patch: Partial<FpsGraphicsState> = {};
  const b = localStorage.getItem(LS_BLOOM);
  if (b !== null) patch.bloom = b === 'true';
  const s = localStorage.getItem(LS_SHADOWS);
  if (s !== null) patch.shadowsHigh = s === 'true';
  const v = localStorage.getItem(LS_VOLUME);
  if (v !== null) {
    const n = Number(v);
    if (!Number.isNaN(n)) patch.masterVolume = Math.max(0, Math.min(1, n));
  }
  const perf = localStorage.getItem(LS_PERF);
  if (perf !== null) patch.performanceMode = perf === 'true';
  const fov = localStorage.getItem(LS_FOV);
  if (fov !== null) {
    const n = Number(fov);
    if (!Number.isNaN(n)) patch.fov = Math.max(70, Math.min(90, n));
  }
  const sens = localStorage.getItem(LS_SENS);
  if (sens !== null) {
    const n = Number(sens);
    if (!Number.isNaN(n)) patch.lookSensitivity = Math.max(0.5, Math.min(2, n));
  }
  const adsSens = localStorage.getItem(LS_ADS_SENS);
  if (adsSens !== null) {
    const n = Number(adsSens);
    if (!Number.isNaN(n)) patch.adsSensitivity = Math.max(0.25, Math.min(1.5, n));
  }
  const cb = localStorage.getItem(LS_COLORBLIND);
  if (cb !== null) patch.colorblindStorm = cb === 'true';
  const touchAim = localStorage.getItem(LS_TOUCH_AIM);
  if (touchAim !== null) patch.touchAimAssist = touchAim === 'true';
  const reduced = localStorage.getItem(LS_REDUCED_MOTION);
  if (reduced !== null) patch.reducedMotion = reduced === 'true';
  const hit = localStorage.getItem(LS_HIT_FEEDBACK);
  if (hit !== null) {
    const n = Number(hit);
    if (n === 0.75 || n === 1 || n === 1.2) patch.hitFeedbackIntensity = n;
  }
  const hud = localStorage.getItem(LS_HUD_SCALE);
  if (hud !== null) {
    const n = Number(hud);
    if (!Number.isNaN(n)) patch.hudScale = clampHudScale(n);
  }
  const botDiff = localStorage.getItem(LS_BOT_DIFFICULTY);
  if (botDiff === 'casual' || botDiff === 'normal' || botDiff === 'sweaty') {
    patch.botDifficulty = botDiff;
  }
  return patch;
}

/** Merge defaults with any persisted graphics settings. */
export function createFpsGraphicsState(): FpsGraphicsState {
  return { ...DEFAULT_GRAPHICS, ...loadFpsGraphicsSettings() };
}

/** Persist a partial graphics patch (only keys present in `patch`). */
export function saveFpsGraphicsSettings(patch: Partial<FpsGraphicsState>): void {
  if (patch.bloom !== undefined) localStorage.setItem(LS_BLOOM, String(patch.bloom));
  if (patch.shadowsHigh !== undefined) localStorage.setItem(LS_SHADOWS, String(patch.shadowsHigh));
  if (patch.masterVolume !== undefined) localStorage.setItem(LS_VOLUME, String(patch.masterVolume));
  if (patch.performanceMode !== undefined) localStorage.setItem(LS_PERF, String(patch.performanceMode));
  if (patch.fov !== undefined) localStorage.setItem(LS_FOV, String(patch.fov));
  if (patch.lookSensitivity !== undefined) localStorage.setItem(LS_SENS, String(patch.lookSensitivity));
  if (patch.adsSensitivity !== undefined) localStorage.setItem(LS_ADS_SENS, String(patch.adsSensitivity));
  if (patch.colorblindStorm !== undefined) localStorage.setItem(LS_COLORBLIND, String(patch.colorblindStorm));
  if (patch.touchAimAssist !== undefined) localStorage.setItem(LS_TOUCH_AIM, String(patch.touchAimAssist));
  if (patch.reducedMotion !== undefined) localStorage.setItem(LS_REDUCED_MOTION, String(patch.reducedMotion));
  if (patch.hitFeedbackIntensity !== undefined) localStorage.setItem(LS_HIT_FEEDBACK, String(patch.hitFeedbackIntensity));
  if (patch.hudScale !== undefined) localStorage.setItem(LS_HUD_SCALE, String(patch.hudScale));
  if (patch.botDifficulty !== undefined) localStorage.setItem(LS_BOT_DIFFICULTY, patch.botDifficulty);
}

/**
 * Fortnite-style graphics settings overlay. Hidden until `open()`.
 */
export class FpsSettingsMenu {
  private readonly getState: () => FpsGraphicsState;
  private readonly getCrosshair: () => FpsCrosshairSettings;
  private readonly applyCrosshair: (patch: Partial<FpsCrosshairSettings>) => void;
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly bloomInput: HTMLInputElement;
  private readonly shadowInput: HTMLInputElement;
  private readonly volumeInput: HTMLInputElement;
  private readonly perfInput: HTMLInputElement;
  private readonly fovInput: HTMLInputElement;
  private readonly fovValue: HTMLSpanElement;
  private readonly sensInput: HTMLInputElement;
  private readonly sensValue: HTMLSpanElement;
  private readonly adsSensInput: HTMLInputElement;
  private readonly adsSensValue: HTMLSpanElement;
  private readonly colorblindInput: HTMLInputElement;
  private readonly touchAimInput: HTMLInputElement;
  private readonly reducedMotionInput: HTMLInputElement;
  private readonly hitFeedbackInput: HTMLSelectElement;
  private readonly hudScaleInput: HTMLInputElement;
  private readonly hudScaleValue: HTMLSpanElement;
  private readonly botDifficultyInput: HTMLSelectElement;
  private readonly hapticsInput: HTMLInputElement;
  private readonly styleButtons: Partial<Record<CrosshairStyle, HTMLButtonElement>> = {};
  private readonly colorSwatches: Partial<Record<CrosshairColor, HTMLButtonElement>> = {};
  private readonly crosshairSizeInput: HTMLInputElement;
  private readonly crosshairSizeValue: HTMLSpanElement;
  private visible = false;
  private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Debounce localStorage writes while sliders are dragged (300ms). */
  private debouncedSaveGraphics(patch: Partial<FpsGraphicsState>): void {
    if (this.saveDebounceTimer !== null) clearTimeout(this.saveDebounceTimer);
    this.saveDebounceTimer = setTimeout(() => {
      this.saveDebounceTimer = null;
      saveFpsGraphicsSettings(patch);
    }, 300);
  }

  constructor(
    mount: HTMLElement,
    getState: () => FpsGraphicsState,
    apply: (patch: Partial<FpsGraphicsState>) => void,
    getCrosshair: () => FpsCrosshairSettings,
    applyCrosshair: (patch: Partial<FpsCrosshairSettings>) => void,
  ) {
    this.getState = getState;
    this.getCrosshair = getCrosshair;
    this.applyCrosshair = applyCrosshair;

    const stored = loadFpsGraphicsSettings();
    if (Object.keys(stored).length > 0) {
      apply(stored);
    }
    const storedCrosshair = loadCrosshairSettings();
    applyCrosshair(storedCrosshair);

    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:100',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'padding:calc(env(safe-area-inset-top,0px) + 16px) calc(env(safe-area-inset-right,0px) + 16px) calc(env(safe-area-inset-bottom,0px) + 16px) calc(env(safe-area-inset-left,0px) + 16px)',
      'background:rgba(4,6,12,0.62)',
      'backdrop-filter:blur(12px)',
      '-webkit-backdrop-filter:blur(12px)',
      'pointer-events:auto',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'width:min(92vw,360px)',
      'max-height:min(88vh,640px)',
      'overflow-y:auto',
      'padding:22px 24px 20px',
      'border-radius:var(--fps-radius,14px)',
      'background:linear-gradient(165deg,rgba(22,30,50,0.97),rgba(10,14,26,0.98))',
      'border:2px solid var(--fps-accent-dim,rgba(46,230,255,0.55))',
      'box-shadow:var(--fps-shadow,0 12px 40px rgba(0,0,0,0.55)),0 0 32px rgba(46,230,255,0.12),inset 0 1px 0 rgba(255,255,255,0.08)',
      'color:var(--fps-text,#e8f4ff)',
      'font:14px var(--fps-font,system-ui,sans-serif)',
      'pointer-events:auto',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'Settings';
    title.style.cssText =
      'font:800 17px var(--fps-font-display,Bungee,system-ui,sans-serif);letter-spacing:0.06em;margin-bottom:4px;color:var(--fps-accent,#2ee6ff)';
    this.panel.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.textContent = 'Graphics · Controls · Crosshair';
    subtitle.style.cssText =
      'font:600 11px var(--fps-font,system-ui,sans-serif);letter-spacing:0.12em;text-transform:uppercase;opacity:0.55;margin-bottom:16px;color:var(--fps-text,#e8f4ff)';
    this.panel.appendChild(subtitle);

    this.bloomInput = document.createElement('input');
    this.bloomInput.type = 'checkbox';
    this.shadowInput = document.createElement('input');
    this.shadowInput.type = 'checkbox';

    const stop = (e: Event): void => {
      e.stopPropagation();
    };
    for (const evt of ['pointerdown', 'mousedown', 'click', 'wheel']) {
      this.backdrop.addEventListener(evt, stop, { passive: evt === 'wheel' });
      this.panel.addEventListener(evt, stop, { passive: evt === 'wheel' });
    }

    this.panel.appendChild(this.row('Bloom', this.bloomInput));
    this.panel.appendChild(this.row('Shadow quality (high)', this.shadowInput));

    const volRow = document.createElement('div');
    volRow.style.cssText =
      'display:flex;flex-direction:column;gap:6px;margin-bottom:14px;user-select:none';
    const volLab = document.createElement('span');
    volLab.textContent = 'SFX volume';
    volLab.style.opacity = '0.92';
    this.volumeInput = document.createElement('input');
    this.volumeInput.type = 'range';
    this.volumeInput.min = '0';
    this.volumeInput.max = '100';
    this.volumeInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.volumeInput.addEventListener('input', () => {
      const t = Number(this.volumeInput.value) / 100;
      this.debouncedSaveGraphics({ masterVolume: t });
      apply({ masterVolume: t });
    });
    volRow.appendChild(volLab);
    volRow.appendChild(this.volumeInput);
    this.panel.appendChild(volRow);

    this.perfInput = document.createElement('input');
    this.perfInput.type = 'checkbox';
    this.panel.appendChild(this.row('Performance mode (lighter bloom)', this.perfInput));
    this.perfInput.addEventListener('change', () => {
      const on = this.perfInput.checked;
      saveFpsGraphicsSettings({ performanceMode: on });
      apply({ performanceMode: on });
    });

    this.fovInput = document.createElement('input');
    this.fovInput.type = 'range';
    this.fovInput.min = '70';
    this.fovInput.max = '90';
    this.fovInput.step = '1';
    this.fovInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.fovValue = document.createElement('span');
    this.fovValue.style.cssText = 'opacity:0.75;font-size:12px';
    const fovRow = this.sliderRow('FOV', this.fovInput, this.fovValue);
    this.fovInput.addEventListener('input', () => {
      const v = Number(this.fovInput.value);
      this.fovValue.textContent = String(v);
      this.debouncedSaveGraphics({ fov: v });
      apply({ fov: v });
    });
    this.panel.appendChild(fovRow);

    this.sensInput = document.createElement('input');
    this.sensInput.type = 'range';
    this.sensInput.min = '50';
    this.sensInput.max = '200';
    this.sensInput.step = '5';
    this.sensInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.sensValue = document.createElement('span');
    this.sensValue.style.cssText = 'opacity:0.75;font-size:12px';
    const sensRow = this.sliderRow('Look sensitivity', this.sensInput, this.sensValue);
    this.sensInput.addEventListener('input', () => {
      const v = Number(this.sensInput.value) / 100;
      this.sensValue.textContent = `${v.toFixed(2)}×`;
      this.debouncedSaveGraphics({ lookSensitivity: v });
      apply({ lookSensitivity: v });
    });
    this.panel.appendChild(sensRow);

    this.adsSensInput = document.createElement('input');
    this.adsSensInput.type = 'range';
    this.adsSensInput.min = '25';
    this.adsSensInput.max = '150';
    this.adsSensInput.step = '5';
    this.adsSensInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.adsSensValue = document.createElement('span');
    this.adsSensValue.style.cssText = 'opacity:0.75;font-size:12px';
    const adsSensRow = this.sliderRow('ADS sensitivity', this.adsSensInput, this.adsSensValue);
    this.adsSensInput.addEventListener('input', () => {
      const v = Number(this.adsSensInput.value) / 100;
      this.adsSensValue.textContent = `${v.toFixed(2)}×`;
      this.debouncedSaveGraphics({ adsSensitivity: v });
      apply({ adsSensitivity: v });
    });
    this.panel.appendChild(adsSensRow);

    this.colorblindInput = document.createElement('input');
    this.colorblindInput.type = 'checkbox';
    this.panel.appendChild(this.row('Colorblind mode', this.colorblindInput));
    this.colorblindInput.addEventListener('change', () => {
      const on = this.colorblindInput.checked;
      saveFpsGraphicsSettings({ colorblindStorm: on });
      apply({ colorblindStorm: on });
    });

    this.touchAimInput = document.createElement('input');
    this.touchAimInput.type = 'checkbox';
    this.panel.appendChild(this.row('Touch aim assist', this.touchAimInput));
    this.touchAimInput.addEventListener('change', () => {
      const on = this.touchAimInput.checked;
      saveFpsGraphicsSettings({ touchAimAssist: on });
      apply({ touchAimAssist: on });
    });

    this.reducedMotionInput = document.createElement('input');
    this.reducedMotionInput.type = 'checkbox';
    this.panel.appendChild(this.row('Reduced motion', this.reducedMotionInput));
    this.reducedMotionInput.addEventListener('change', () => {
      const on = this.reducedMotionInput.checked;
      saveFpsGraphicsSettings({ reducedMotion: on });
      apply({ reducedMotion: on });
    });

    this.hitFeedbackInput = this.select([
      ['0.75', 'Low'],
      ['1', 'Normal'],
      ['1.2', 'High'],
    ]);
    this.panel.appendChild(this.selectRow('Hit feedback intensity', this.hitFeedbackInput));
    this.hitFeedbackInput.addEventListener('change', () => {
      const v = Number(this.hitFeedbackInput.value) as FpsHitFeedbackIntensity;
      saveFpsGraphicsSettings({ hitFeedbackIntensity: v });
      apply({ hitFeedbackIntensity: v });
    });

    this.hudScaleInput = document.createElement('input');
    this.hudScaleInput.type = 'range';
    this.hudScaleInput.min = String(Math.round(HUD_SCALE_MIN * 100));
    this.hudScaleInput.max = String(Math.round(HUD_SCALE_MAX * 100));
    this.hudScaleInput.step = '1';
    this.hudScaleInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.hudScaleValue = document.createElement('span');
    this.hudScaleValue.style.cssText = 'opacity:0.75;font-size:12px';
    const hudScaleRow = this.sliderRow('HUD scale', this.hudScaleInput, this.hudScaleValue);
    this.hudScaleInput.addEventListener('input', () => {
      const v = clampHudScale(Number(this.hudScaleInput.value) / 100);
      this.hudScaleValue.textContent = `${Math.round(v * 100)}%`;
      this.debouncedSaveGraphics({ hudScale: v });
      apply({ hudScale: v });
    });
    this.panel.appendChild(hudScaleRow);

    this.botDifficultyInput = this.select([
      ['casual', 'Casual'],
      ['normal', 'Normal'],
      ['sweaty', 'Sweaty'],
    ]);
    this.panel.appendChild(this.selectRow('Bot difficulty', this.botDifficultyInput));
    this.botDifficultyInput.addEventListener('change', () => {
      const v = this.botDifficultyInput.value as BotDifficultyTier;
      localStorage.setItem(LS_BOT_DIFFICULTY, v);
      apply({ botDifficulty: v });
    });

    this.hapticsInput = document.createElement('input');
    this.hapticsInput.type = 'checkbox';
    this.panel.appendChild(this.row('Haptics (vibration)', this.hapticsInput));
    this.hapticsInput.addEventListener('change', () => {
      FpsHaptics.setEnabled(this.hapticsInput.checked);
    });

    const crosshairTitle = document.createElement('div');
    crosshairTitle.textContent = 'Crosshair';
    crosshairTitle.style.cssText =
      'font-weight:700;font-size:13px;letter-spacing:0.08em;margin:14px 0 10px;padding-top:12px;border-top:1px solid rgba(255,255,255,0.08);color:var(--fps-accent,#2ee6ff)';
    this.panel.appendChild(crosshairTitle);

    const styleRow = document.createElement('div');
    styleRow.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-bottom:12px;user-select:none';
    const styleLab = document.createElement('span');
    styleLab.textContent = 'Style';
    styleLab.style.opacity = '0.92';
    styleRow.appendChild(styleLab);
    const styleBtns = document.createElement('div');
    styleBtns.style.cssText = 'display:flex;gap:6px';
    for (const style of ['dot', 'cross', 'circle'] as CrosshairStyle[]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = style.charAt(0).toUpperCase() + style.slice(1);
      btn.dataset.style = style;
      btn.style.cssText =
        'flex:1;padding:8px 8px;border-radius:var(--fps-radius-sm,8px);border:1px solid var(--fps-accent-dim,rgba(46,230,255,0.35));background:var(--fps-panel-bg,rgba(12,18,32,0.85));color:var(--fps-text,#e8f4ff);font:12px var(--fps-font,system-ui,sans-serif);cursor:pointer;transition:border-color 0.15s,background 0.15s';
      btn.addEventListener('click', () => {
        saveCrosshairSettings({ ...this.getCrosshair(), style });
        this.applyCrosshair({ style });
        this.syncCrosshairInputs();
      });
      styleBtns.appendChild(btn);
      this.styleButtons[style] = btn;
    }
    styleRow.appendChild(styleBtns);
    this.panel.appendChild(styleRow);

    const colorRow = document.createElement('div');
    colorRow.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-bottom:12px;user-select:none';
    const colorLab = document.createElement('span');
    colorLab.textContent = 'Color';
    colorLab.style.opacity = '0.92';
    colorRow.appendChild(colorLab);
    const swatchWrap = document.createElement('div');
    swatchWrap.style.cssText = 'display:flex;gap:8px';
    for (const color of ['white', 'cyan', 'green', 'red'] as CrosshairColor[]) {
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.dataset.color = color;
      sw.title = color.charAt(0).toUpperCase() + color.slice(1);
      sw.style.cssText = [
        'width:28px;height:28px;border-radius:50%;cursor:pointer',
        'border:2px solid rgba(255,255,255,0.25)',
        `background:${crosshairColorCss(color)}`,
        'box-shadow:0 0 0 1px rgba(0,0,0,0.35)',
      ].join(';');
      sw.addEventListener('click', () => {
        saveCrosshairSettings({ ...this.getCrosshair(), color });
        this.applyCrosshair({ color });
        this.syncCrosshairInputs();
      });
      swatchWrap.appendChild(sw);
      this.colorSwatches[color] = sw;
    }
    colorRow.appendChild(swatchWrap);
    this.panel.appendChild(colorRow);

    this.crosshairSizeInput = document.createElement('input');
    this.crosshairSizeInput.type = 'range';
    this.crosshairSizeInput.min = '80';
    this.crosshairSizeInput.max = '140';
    this.crosshairSizeInput.step = '5';
    this.crosshairSizeInput.style.cssText = 'width:100%;accent-color:#2ee6ff';
    this.crosshairSizeValue = document.createElement('span');
    this.crosshairSizeValue.style.cssText = 'opacity:0.75;font-size:12px';
    const crosshairSizeRow = this.sliderRow('Crosshair size', this.crosshairSizeInput, this.crosshairSizeValue);
    this.crosshairSizeInput.addEventListener('input', () => {
      const size = Number(this.crosshairSizeInput.value) / 100;
      this.crosshairSizeValue.textContent = `${size.toFixed(2)}×`;
      if (this.saveDebounceTimer !== null) clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = setTimeout(() => {
        this.saveDebounceTimer = null;
        saveCrosshairSettings({ ...this.getCrosshair(), size });
      }, 300);
      this.applyCrosshair({ size });
    });
    this.panel.appendChild(crosshairSizeRow);

    this.bloomInput.addEventListener('change', () => {
      const on = this.bloomInput.checked;
      saveFpsGraphicsSettings({ bloom: on });
      apply({ bloom: on });
    });
    this.shadowInput.addEventListener('change', () => {
      const on = this.shadowInput.checked;
      saveFpsGraphicsSettings({ shadowsHigh: on });
      apply({ shadowsHigh: on });
    });

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);

    const st0 = this.getState();
    this.bloomInput.checked = st0.bloom;
    this.shadowInput.checked = st0.shadowsHigh;
    this.volumeInput.value = String(Math.round((st0.masterVolume ?? 0.85) * 100));
    this.perfInput.checked = st0.performanceMode ?? false;
    this.syncGameplayInputs(st0);
    this.syncCrosshairInputs();
  }

  private syncCrosshairInputs(): void {
    const ch = this.getCrosshair();
    for (const style of ['dot', 'cross', 'circle'] as CrosshairStyle[]) {
      const btn = this.styleButtons[style];
      if (!btn) continue;
      const active = ch.style === style;
      btn.style.borderColor = active ? 'var(--fps-accent,rgba(46,230,255,0.95))' : 'var(--fps-accent-dim,rgba(46,230,255,0.35))';
      btn.style.background = active ? 'rgba(46,230,255,0.18)' : 'var(--fps-panel-bg,rgba(12,18,32,0.85))';
      btn.style.color = active ? 'var(--fps-accent,#2ee6ff)' : 'var(--fps-text,#e8f4ff)';
    }
    for (const color of ['white', 'cyan', 'green', 'red'] as CrosshairColor[]) {
      const sw = this.colorSwatches[color];
      if (!sw) continue;
      sw.style.outline = ch.color === color ? '2px solid #2ee6ff' : 'none';
      sw.style.outlineOffset = '2px';
    }
    this.crosshairSizeInput.value = String(Math.round(ch.size * 100));
    this.crosshairSizeValue.textContent = `${ch.size.toFixed(2)}×`;
  }

  private syncGameplayInputs(st: FpsGraphicsState): void {
    this.fovInput.value = String(Math.round(st.fov ?? 76));
    this.fovValue.textContent = String(Math.round(st.fov ?? 76));
    const sens = st.lookSensitivity ?? 1;
    this.sensInput.value = String(Math.round(sens * 100));
    this.sensValue.textContent = `${sens.toFixed(2)}×`;
    const adsSens = st.adsSensitivity ?? 0.7;
    this.adsSensInput.value = String(Math.round(adsSens * 100));
    this.adsSensValue.textContent = `${adsSens.toFixed(2)}×`;
    this.colorblindInput.checked = st.colorblindStorm ?? false;
    this.touchAimInput.checked = st.touchAimAssist ?? false;
    this.reducedMotionInput.checked = st.reducedMotion ?? false;
    this.hitFeedbackInput.value = String(st.hitFeedbackIntensity ?? 1);
    const hudScale = clampHudScale(st.hudScale ?? HUD_SCALE_DEFAULT);
    this.hudScaleInput.value = String(Math.round(hudScale * 100));
    this.hudScaleValue.textContent = `${Math.round(hudScale * 100)}%`;
    this.botDifficultyInput.value = st.botDifficulty ?? 'normal';
    this.hapticsInput.checked = FpsHaptics.isEnabled();
  }

  private select(options: [value: string, label: string][]): HTMLSelectElement {
    const input = document.createElement('select');
    input.style.cssText =
      'min-width:94px;padding:6px 10px;border-radius:var(--fps-radius-sm,8px);border:1px solid var(--fps-accent-dim,rgba(46,230,255,0.45));background:var(--fps-panel-bg,rgba(12,18,32,0.92));color:var(--fps-text,#e8f4ff);font:12px var(--fps-font,system-ui,sans-serif);cursor:pointer;outline:none';
    for (const [value, label] of options) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      input.appendChild(opt);
    }
    return input;
  }

  private selectRow(label: string, input: HTMLSelectElement): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:12px;user-select:none';
    const lab = document.createElement('span');
    lab.textContent = label;
    lab.style.cssText = 'opacity:0.92';
    row.appendChild(lab);
    row.appendChild(input);
    return row;
  }

  private sliderRow(label: string, input: HTMLInputElement, valueEl: HTMLSpanElement): HTMLDivElement {
    // Phase 34X-22: focus-visible ring on settings controls
    const row = document.createElement('div');
    row.style.cssText =
      'display:flex;flex-direction:column;gap:6px;margin-bottom:14px;user-select:none';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:8px';
    const lab = document.createElement('span');
    lab.textContent = label;
    lab.style.opacity = '0.92';
    head.appendChild(lab);
    head.appendChild(valueEl);
    row.appendChild(head);
    input.style.outline = 'none';
    input.addEventListener('focus', () => {
      input.style.boxShadow = '0 0 0 2px rgba(46,230,255,0.65)';
    });
    input.addEventListener('blur', () => {
      input.style.boxShadow = '';
    });
    row.appendChild(input);
    return row;
  }

  private row(label: string, input: HTMLInputElement): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:12px';

    const wrap = document.createElement('label');
    wrap.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:16px;flex:1;cursor:pointer;user-select:none';

    const lab = document.createElement('span');
    lab.textContent = label;
    lab.style.cssText = 'opacity:0.92';

    input.style.cssText =
      'width:18px;height:18px;accent-color:var(--fps-accent,#2ee6ff);cursor:pointer;flex-shrink:0';

    wrap.appendChild(lab);
    wrap.appendChild(input);
    row.appendChild(wrap);
    return row;
  }

  open(): void {
    this.visible = true;
    this.backdrop.style.display = 'flex';
    const st = this.getState();
    this.bloomInput.checked = st.bloom;
    this.shadowInput.checked = st.shadowsHigh;
    this.volumeInput.value = String(Math.round((st.masterVolume ?? 0.85) * 100));
    this.perfInput.checked = st.performanceMode ?? false;
    this.syncGameplayInputs(st);
    this.syncCrosshairInputs();
  }

  close(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.saveDebounceTimer !== null) clearTimeout(this.saveDebounceTimer);
    this.backdrop.remove();
  }
}
